import { useState, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download, FileText, Calculator, IndianRupee } from "lucide-react";

const fmt = (n: number) => `₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const GSTReportsPage = () => {
  const { vouchers, ledgers } = useAccounting();

  const salesVouchers = vouchers.filter((v) => v.type === "Sales" && v.status === "Approved");
  const purchaseVouchers = vouchers.filter((v) => v.type === "Purchase" && v.status === "Approved");
  const creditNotes = vouchers.filter((v) => v.type === "Credit Note" && v.status === "Approved");
  const debitNotes = vouchers.filter((v) => v.type === "Debit Note" && v.status === "Approved");

  const salesTotals = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0;
    salesVouchers.forEach((v) => {
      const gstAmt = (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0);
      taxable += v.totalDebit - gstAmt;
      cgst += v.cgst || 0;
      sgst += v.sgst || 0;
      igst += v.igst || 0;
    });
    return { taxable, cgst, sgst, igst, total: taxable + cgst + sgst + igst };
  }, [salesVouchers]);

  const purchaseTotals = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0;
    purchaseVouchers.forEach((v) => {
      taxable += v.totalDebit - (v.gstAmount || 0);
      cgst += v.cgst || 0;
      sgst += v.sgst || 0;
      igst += v.igst || 0;
    });
    return { taxable, cgst, sgst, igst, total: taxable + cgst + sgst + igst };
  }, [purchaseVouchers]);

  const netPayable = (salesTotals.cgst + salesTotals.sgst + salesTotals.igst) - (purchaseTotals.cgst + purchaseTotals.sgst + purchaseTotals.igst);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">GST Reports</h1>
          <p className="text-sm text-muted-foreground">GSTR-1, GSTR-3B, registers, and ITC reports</p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" /> Export Reports
        </Button>
      </div>

      <Tabs defaultValue="gstr1">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="gstr1">GSTR-1</TabsTrigger>
          <TabsTrigger value="gstr3b">GSTR-3B</TabsTrigger>
          <TabsTrigger value="sales-register">Sales Register</TabsTrigger>
          <TabsTrigger value="purchase-register">Purchase Register</TabsTrigger>
          <TabsTrigger value="itc">Input Tax Credit</TabsTrigger>
        </TabsList>

        {/* GSTR-1 */}
        <TabsContent value="gstr1" className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
            <strong className="text-foreground">GSTR-1</strong> — Statement of outward supplies. All B2B and B2C sales invoices for the period.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <SummaryCard label="Total Invoices" value={String(salesVouchers.length)} />
            <SummaryCard label="Taxable Value" value={fmt(salesTotals.taxable)} />
            <SummaryCard label="Total Tax" value={fmt(salesTotals.cgst + salesTotals.sgst + salesTotals.igst)} />
            <SummaryCard label="Invoice Value" value={fmt(salesTotals.total)} />
          </div>

          <div className="rounded-lg bg-card border border-border shadow-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Invoice No</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Customer</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">GSTIN</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Taxable</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">CGST</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">SGST</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">IGST</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {salesVouchers.map((v) => {
                  const party = ledgers.find((l) => l.id === v.partyLedgerId);
                  const gstAmt = (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0);
                  const taxable = v.totalDebit - gstAmt;
                  return (
                    <tr key={v.id} className="border-t border-border/50">
                      <td className="py-3 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.invoiceNumber || v.voucherNumber}</td>
                      <td className="py-3 px-4 text-foreground">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                      <td className="py-3 px-4 text-foreground">{v.partyName}</td>
                      <td className="py-3 px-4 text-muted-foreground font-mono text-xs">{party?.gstin || "—"}</td>
                      <td className="py-3 px-4 text-right text-foreground">{fmt(taxable)}</td>
                      <td className="py-3 px-4 text-right text-foreground">{v.cgst ? fmt(v.cgst) : "—"}</td>
                      <td className="py-3 px-4 text-right text-foreground">{v.sgst ? fmt(v.sgst) : "—"}</td>
                      <td className="py-3 px-4 text-right text-foreground">{v.igst ? fmt(v.igst) : "—"}</td>
                      <td className="py-3 px-4 text-right font-semibold text-foreground">{fmt(v.totalDebit)}</td>
                    </tr>
                  );
                })}
                {salesVouchers.length === 0 && (
                  <tr><td colSpan={9} className="py-12 text-center text-muted-foreground">No sales invoices found</td></tr>
                )}
              </tbody>
              {salesVouchers.length > 0 && (
                <tfoot>
                  <tr className="bg-muted/50 font-semibold">
                    <td colSpan={4} className="py-3 px-4 text-foreground">Total</td>
                    <td className="py-3 px-4 text-right text-foreground">{fmt(salesTotals.taxable)}</td>
                    <td className="py-3 px-4 text-right text-foreground">{fmt(salesTotals.cgst)}</td>
                    <td className="py-3 px-4 text-right text-foreground">{fmt(salesTotals.sgst)}</td>
                    <td className="py-3 px-4 text-right text-foreground">{fmt(salesTotals.igst)}</td>
                    <td className="py-3 px-4 text-right text-foreground">{fmt(salesTotals.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </TabsContent>

        {/* GSTR-3B */}
        <TabsContent value="gstr3b" className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
            <strong className="text-foreground">GSTR-3B</strong> — Summary return with tax liability and ITC claims.
          </div>

          <div className="space-y-4">
            {/* 3.1 - Outward Supplies */}
            <div className="rounded-lg bg-card border border-border shadow-card p-5">
              <h3 className="font-semibold text-foreground mb-3">3.1 — Outward Supplies & Tax</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground text-xs uppercase">Nature of Supply</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">Taxable Value</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">IGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">CGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">SGST</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 text-foreground">Outward taxable supplies (other than zero/nil rated & exempted)</td>
                    <td className="py-2.5 text-right font-medium text-foreground">{fmt(salesTotals.taxable)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(salesTotals.igst)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(salesTotals.cgst)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(salesTotals.sgst)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 4 - ITC */}
            <div className="rounded-lg bg-card border border-border shadow-card p-5">
              <h3 className="font-semibold text-foreground mb-3">4 — Eligible ITC</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground text-xs uppercase">Details</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">IGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">CGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">SGST</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 text-foreground">All other ITC</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(purchaseTotals.igst)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(purchaseTotals.cgst)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(purchaseTotals.sgst)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 6 - Payment */}
            <div className="rounded-lg bg-card border border-border shadow-card p-5">
              <h3 className="font-semibold text-foreground mb-3">6.1 — Tax Payable / (Refund)</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground text-xs uppercase">Description</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">IGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">CGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">SGST</th>
                    <th className="text-right py-2 text-muted-foreground text-xs uppercase">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 text-foreground">Output Tax</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(salesTotals.igst)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(salesTotals.cgst)}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(salesTotals.sgst)}</td>
                    <td className="py-2.5 text-right font-medium text-foreground">{fmt(salesTotals.cgst + salesTotals.sgst + salesTotals.igst)}</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 text-foreground">Less: ITC</td>
                    <td className="py-2.5 text-right text-foreground">({fmt(purchaseTotals.igst)})</td>
                    <td className="py-2.5 text-right text-foreground">({fmt(purchaseTotals.cgst)})</td>
                    <td className="py-2.5 text-right text-foreground">({fmt(purchaseTotals.sgst)})</td>
                    <td className="py-2.5 text-right font-medium text-foreground">({fmt(purchaseTotals.cgst + purchaseTotals.sgst + purchaseTotals.igst)})</td>
                  </tr>
                  <tr className="font-bold border-t border-border">
                    <td className="py-2.5 text-foreground">Net Tax Payable (Cash)</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(Math.max(0, salesTotals.igst - purchaseTotals.igst))}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(Math.max(0, salesTotals.cgst - purchaseTotals.cgst))}</td>
                    <td className="py-2.5 text-right text-foreground">{fmt(Math.max(0, salesTotals.sgst - purchaseTotals.sgst))}</td>
                    <td className={`py-2.5 text-right ${netPayable > 0 ? "text-destructive font-bold" : "text-foreground"}`}>
                      {fmt(Math.max(0, netPayable))}
                    </td>
                  </tr>
                  {netPayable < 0 && (
                    <tr className="font-semibold text-success bg-success/5 border-t border-success/20">
                      <td className="py-2.5 px-1">Excess ITC Carried Forward</td>
                      <td className="py-2.5 text-right">{fmt(Math.max(0, purchaseTotals.igst - salesTotals.igst))}</td>
                      <td className="py-2.5 text-right">{fmt(Math.max(0, purchaseTotals.cgst - salesTotals.cgst))}</td>
                      <td className="py-2.5 text-right">{fmt(Math.max(0, purchaseTotals.sgst - salesTotals.sgst))}</td>
                      <td className="py-2.5 text-right font-bold text-success">
                        {fmt(Math.abs(netPayable))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Sales Register */}
        <TabsContent value="sales-register" className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
            <strong className="text-foreground">GST Sales Register</strong> — Detailed record of all GST sales with tax breakup.
          </div>
          <RegisterTable vouchers={salesVouchers} ledgers={ledgers} type="Sales" />
        </TabsContent>

        {/* Purchase Register */}
        <TabsContent value="purchase-register" className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
            <strong className="text-foreground">GST Purchase Register</strong> — Detailed record of all GST purchases with ITC breakup.
          </div>
          <RegisterTable vouchers={purchaseVouchers} ledgers={ledgers} type="Purchase" />
        </TabsContent>

        {/* ITC Report */}
        <TabsContent value="itc" className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
            <strong className="text-foreground">Input Tax Credit Report</strong> — Summary of all ITC claimed from purchases.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <SummaryCard label="Total Purchases" value={String(purchaseVouchers.length)} />
            <SummaryCard label="CGST ITC" value={fmt(purchaseTotals.cgst)} />
            <SummaryCard label="SGST ITC" value={fmt(purchaseTotals.sgst)} />
            <SummaryCard label="IGST ITC" value={fmt(purchaseTotals.igst)} />
          </div>

          <div className="rounded-lg bg-card border border-border shadow-card p-5 space-y-3">
            <h3 className="font-semibold text-foreground">ITC Utilisation Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm py-2 border-b border-border/50">
                <span className="text-foreground">Total ITC Available</span>
                <span className="font-semibold text-foreground">{fmt(purchaseTotals.cgst + purchaseTotals.sgst + purchaseTotals.igst)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-border/50">
                <span className="text-foreground">Output Tax Liability</span>
                <span className="font-semibold text-foreground">{fmt(salesTotals.cgst + salesTotals.sgst + salesTotals.igst)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 font-bold">
                <span className="text-foreground">Net Position</span>
                <span className={netPayable > 0 ? "text-destructive" : "text-success"}>
                  {netPayable > 0 ? `Pay: ${fmt(netPayable)}` : `Refund: ${fmt(Math.abs(netPayable))}`}
                </span>
              </div>
            </div>
          </div>

          <RegisterTable vouchers={purchaseVouchers} ledgers={ledgers} type="Purchase" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 rounded-lg bg-card border border-border shadow-card">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold font-display text-foreground mt-1">{value}</div>
    </div>
  );
}

function RegisterTable({ vouchers, ledgers, type }: { vouchers: any[]; ledgers: any[]; type: string }) {
  return (
    <div className="rounded-lg bg-card border border-border shadow-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Voucher No</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">{type === "Sales" ? "Customer" : "Supplier"}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">GSTIN</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Taxable</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">CGST</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">SGST</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">IGST</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody>
            {vouchers.map((v: any) => {
              const party = ledgers.find((l: any) => l.id === v.partyLedgerId);
              const gstAmt = (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0);
              const taxable = v.totalDebit - gstAmt;
              return (
                <tr key={v.id} className="border-t border-border/50">
                  <td className="py-3 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.invoiceNumber || v.voucherNumber}</td>
                  <td className="py-3 px-4 text-foreground">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="py-3 px-4 text-foreground">{v.partyName || "—"}</td>
                  <td className="py-3 px-4 text-muted-foreground font-mono text-xs">{party?.gstin || "—"}</td>
                  <td className="py-3 px-4 text-right text-foreground">{fmt(taxable)}</td>
                  <td className="py-3 px-4 text-right text-foreground">{v.cgst ? fmt(v.cgst) : "—"}</td>
                  <td className="py-3 px-4 text-right text-foreground">{v.sgst ? fmt(v.sgst) : "—"}</td>
                  <td className="py-3 px-4 text-right text-foreground">{v.igst ? fmt(v.igst) : "—"}</td>
                  <td className="py-3 px-4 text-right font-semibold text-foreground">{fmt(v.totalDebit)}</td>
                </tr>
              );
            })}
            {vouchers.length === 0 && (
              <tr><td colSpan={9} className="py-12 text-center text-muted-foreground">No entries found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GSTReportsPage;
